import { Alert, AlertTitle } from '@/global/components/Alert'
import IconButton from '@/global/components/Button/IconButton'
import { Cartao } from '@/global/components/Cartao'
import Grid from '@/global/components/ui/Grid'
import { FormField, FormFieldSelect } from '@/global/components/Form'
import { useAuth } from '@/hooks'
import { cartoes } from '@/service/api'
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/global/components/ui/tabs'
import {
    CreditCardIcon,
    ScanBarcodeIcon,
    Trash2Icon,
    WalletIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import useCheckoutForm from '../../hooks/useCheckoutForm'

interface IProps {
    disabled?: 'cartao' | 'boleto'
}

/**
 * * Utiliza `Customer.myId` para buscar os cartões salvos do cliente
 * * Salva os dados do cartão no campo `Card.myId`
 * * Se o cartão for novo, salva os dados do cartão no campo `Card`
 * * Se o método de pagamento for boleto, altera o campo `mainPaymentMethodId` para `'boleto'`
 */
export default function AbasFormaPagamento({ disabled }: IProps) {
    const { setValue } = useCheckoutForm()

    const AbaCartoes = useAbaCartoes(),
        AbaNovoCartao = useAbaNovoCartao(),
        AbaBoleto = useAbaBoleto()

    const handleTabChange = (newValue: string) => {
        setValue('Card.myId', '')
        setTab(newValue)
    }

    const [tab, setTab] = useState<string>(
        disabled === 'boleto' ? AbaNovoCartao.name : AbaBoleto.name,
    )

    useEffect(() => {
        if ([AbaCartoes.name, AbaNovoCartao.name].includes(tab as any))
            setValue('mainPaymentMethodId', 'creditcard')
        else setValue('mainPaymentMethodId', 'boleto')
    }, [tab])

    useEffect(() => {
        if (!AbaCartoes.visible && tab === AbaCartoes.name) {
            setTab(AbaNovoCartao.name)
        }
    }, [AbaCartoes.visible])

    return (
        <Tabs value={tab} onValueChange={handleTabChange} className="contents">
            <div className="border-b">
                <TabsList>
                    {disabled !== 'cartao' &&
                        AbaCartoes.visible &&
                        AbaCartoes.Select}
                    {disabled !== 'cartao' && AbaNovoCartao.Select}
                    {disabled !== 'boleto' && AbaBoleto.Select}
                </TabsList>
            </div>

            {disabled !== 'cartao' && AbaCartoes.Aba}
            {disabled !== 'cartao' && AbaNovoCartao.Aba}
            {disabled !== 'boleto' && AbaBoleto.Aba}
        </Tabs>
    )
}

function useAbaCartoes() {
    const name = 'cartoes' as const
    const { watch } = useCheckoutForm()

    const customerMyId = watch('Customer.myId')
    const { auth } = useAuth()

    const { data } = cartoes.useFetch({
        enabled: !!customerMyId && auth,
        refetchOnWindowFocus: false,
    })
    const cartoesSalvos = Array.isArray(data) ? data : []

    const { mutateAsync } = cartoes.mutations.useDelete({
        invalidateQuery: true,
        onSuccess: () => {
            toast.success('Cartão removido com sucesso!')
        },
        onError: (e: any) => {
            toast.error(e.message)
        },
    })

    return {
        Select: (
            <TabsTrigger
                value={name}
                className="text-[length:0] sm:text-[length:inherit]"
                title="Cartões salvos"
            >
                <WalletIcon />
                Cartões salvos
            </TabsTrigger>
        ),
        Aba: (
            <TabsContent value={name} className="p-0 py-6">
                <div className="mt-2">
                    <FormFieldSelect
                        name="Card.myId"
                        label="Selecione um cartão"
                        options={cartoesSalvos.map(card => ({
                            label: [
                                <span key={card.id}>{card.nome}</span>,
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation()
                                        mutateAsync(card.id)
                                    }}
                                    key={card.id + 'delete'}
                                    title="Remover cartão"
                                    className="z-1 ml-2"
                                >
                                    <Trash2Icon className="text-error" />
                                </IconButton>,
                            ],
                            value: card.myId,
                        }))}
                        placeholder="Selecione um cartão"
                        required
                    />
                </div>
            </TabsContent>
        ),
        name,
        visible: cartoesSalvos.length > 0,
    }
}

function useAbaNovoCartao() {
    const name = 'new-creditcard' as const
    const { watch } = useCheckoutForm()
    const card = watch('Card')

    return {
        Select: (
            <TabsTrigger
                value={name}
                className="text-[length:0] sm:text-[length:inherit]"
                title="Novo cartão de crédito"
            >
                <CreditCardIcon />
                Cartão de crédito
            </TabsTrigger>
        ),
        Aba: (
            <TabsContent value={name} className="p-0 py-6">
                <div className="mt-2 flex flex-row gap-4">
                    <div className="hidden md:flex items-center justify-center">
                        <Cartao {...card} />
                    </div>
                    <Grid container>
                        <Grid item xs={12}>
                            <FormField
                                name="Card.holder"
                                label="Nome do titular"
                                uppercase
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormField
                                name="Card.number"
                                label="Número do cartão"
                                options={{
                                    creditCard: true,
                                }}
                                required
                            />
                        </Grid>
                        <Grid item sm={8} xs={12}>
                            <FormField
                                name="Card.expiresAt"
                                label="Validade"
                                placeholder="MM/YYYY"
                                options={{
                                    date: true,
                                    datePattern: ['m', 'Y'],
                                }}
                                required
                            />
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <FormField
                                name="Card.cvv"
                                label="CVV"
                                options={{ blocks: [3], numericOnly: true }}
                                required
                            />
                        </Grid>
                    </Grid>
                </div>
            </TabsContent>
        ),
        name,
    }
}

function useAbaBoleto() {
    const name = 'boleto-pix' as const
    return {
        Select: (
            <TabsTrigger
                value={name}
                className="text-[length:0] sm:text-[length:inherit]"
                title="Boleto ou PIX"
            >
                <ScanBarcodeIcon />
                Boleto/PIX
            </TabsTrigger>
        ),
        Aba: (
            <TabsContent value={name} className="p-0 py-6">
                <div className="mt-2">
                    <Grid container>
                        <Grid item xs={12}>
                            <Alert severity="info" className="text-balance">
                                <AlertTitle>Atenção</AlertTitle>
                                <p>
                                    Você será redirecionado para a o boleto
                                    gerado após a conclusão do contrato, onde
                                    poderá efetuar o pagamento pelo codigo de
                                    barras ou PIX.
                                </p>
                            </Alert>
                        </Grid>
                    </Grid>
                </div>
            </TabsContent>
        ),
        name,
    }
}
